import { useDispatch, useSelector } from "react-redux";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import "./admin.scss";
import { withUser } from "hoc/withUser";
import UserMenu from "components/UserMenu/UserMenu";
import { getAuthUser } from "redux/selectors/getUser";
import Logo from "components/Logo/Logo";
import {
  MdSupervisorAccount,
  MdOutlineGroups,
  MdOutlineModeComment,
  MdLogout,
  MdAirplaneTicket,
  MdSettings,
  MdOutlineAirplaneTicket,
  MdLocalActivity,
  MdHotel,
  MdPayments,
  MdBarChart,
  MdOutlineNotifications,
  MdShoppingCart,
  MdBookmarks,
  MdCode,
  MdIntegrationInstructions,
  MdChat,
  MdInsights,
  MdOutlineConfirmationNumber,
  MdOutlineMap,
  MdOutlineAssignment,
  MdOutlineLocalOffer,
  MdOutlineLoyalty,
  MdOutlineSell,
  MdOutlineMonetizationOn,
  MdTrain,
  MdInbox,
} from "react-icons/md";
import { useEffect, useRef } from "react";
import { Alert, Box, Button, Collapse, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { FaArrowLeft, FaArrowRight, FaBars, FaFire, FaPlus } from "react-icons/fa";
import CurrencyRates from "components/CurrencyRates";
import { getAgents } from "redux/selectors/getAgents";
import { useGetOrg } from "hooks/useGetOrg";
import { useGetAIAgents } from "hooks/useGetAIAgents";
import { setDataAction } from "redux/actions/agentsActions";
import { updateSidebarAction } from "redux/reducers/appLayout";
import { useGetSubscription } from "hooks/useGetSubscription";


const AdminLayout = () => {
  const authUser = useSelector(getAuthUser);
  const agentsState = useSelector(getAgents)
  const sidebar = useRef(null);
  const burger = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarOpen = useSelector((state) => state.app.sidebar.open);
  const dispatch = useDispatch();

  const { data: org, isLoading: isOrgLoading } = useGetOrg(localStorage.getItem('orgID'));

  const { data: agentsData, isLoading: isAgentsLoading, error: agentsError } = useGetAIAgents();

  const { data: subscriptionData, isLoading: isSubscriptionLoading, error: subscriptionError, isFetched: isSubscriptionFetched } = useGetSubscription(localStorage.getItem('orgID'));

  useEffect(() => {
    dispatch(setDataAction(agentsData?.rows));
  }, [agentsData]);

  const adminMenu = [
    {
      icon: MdInbox,
      path: "inbox",
      label: "Входящие",
    },
    {
      icon: MdBarChart,
      path: "stats",
      label: "Аналитика",
    },
    {
      icon: MdOutlineMonetizationOn,
      path: "leads",
      label: "Сделки",
    },
    {
      icon: MdOutlineConfirmationNumber,
      path: "tickets",
      label: "Обращения",
      children: [
        {
          icon: MdOutlineConfirmationNumber,
          path: "tickets/list",
          label: "Обращения",
        },
        // Карта обращений по филиалам
        {
          icon: MdOutlineMap,
          path: "tickets/map",
          label: "Карта",
        },
        // Темы обращений
        {
          icon: MdOutlineConfirmationNumber,
          path: "tickets/categories",
          label: "Тематика",
        },
        // Шаблоны ответов (canned replies)
        {
          icon: MdOutlineConfirmationNumber,
          path: "tickets/templates",
          label: "Шаблоны ответов",
        },
      ]
    },
    {
      icon: MdOutlineAssignment,
      path: "tasks",
      label: "Задачи",
    },
    {
      icon: MdSupervisorAccount,
      path: "customers",
      label: 'Управление клиентами',
      children: [
        {
          icon: MdSupervisorAccount,
          path: "customers/all",
          label: "Клиенты",
        },
        {
          icon: MdSupervisorAccount,
          path: "customers/passport",
          label: "Просроченные паспорта",
        },
        {
          icon: MdSupervisorAccount,
          path: "customers/visas",
          label: "Просроченные визы",
        },
        {
          icon: MdSupervisorAccount,
          path: "customers/debtors",
          label: "Реестр должникиов",
        },
      ]
    },
    {
      // Автоматизация
      icon: MdInsights,
      path: "automations",
      label: "Автоматизация"
    },
    // Chats
    {
      icon: MdChat,
      path: "chats",
      label: "Чаты",
    },
    {
      icon: MdLocalActivity,
      path: "tour-packages",
      label: "Управление турпакеты",
      children: [
        {
          icon: MdLocalActivity,
          path: "tour-packages/list",
          label: "Турпакеты",
        },
        {
          icon: MdHotel,
          path: "tour-packages/hotels",
          label: "Отели",
        },
        {
          icon: MdOutlineAirplaneTicket,
          path: "tour-packages/airlines",
          label: "Авиакомпании",
        },
        {
          icon: MdAirplaneTicket,
          path: "tour-packages/flights",
          label: "Расписание рейсов",
        },
        {
          icon: MdBookmarks,
          path: "tour-packages/attributes",
          label: "Атрибуты турпакета",
        },
        {
          icon: MdBookmarks,
          path: "tour-packages/tour-schedules",
          label: "Расписание туров",
        },
        {
          icon: MdBookmarks,
          path: "tour-packages/tourpackage-categories",
          label: "Категории турпакетов",
        },
      ]
    },
    {
      icon: MdPayments,
      path: "sub-orders",
      label: "Управление заказами",
      children: [
        {
          icon: MdPayments,
          path: "sub-orders/list",
          label: "Заказы",
        },
        {
          icon: MdPayments,
          path: "sub-orders/payments",
          label: "История платежей",
        },
        {
          icon: MdPayments,
          path: "sub-orders/unpaid-clients",
          label: "Графа должников",
        },
      ]
    },
    {
      icon: MdAirplaneTicket,
      path: "group-trips",
      label: "Поездки"
    },
  ];

  const aiAgentsMenu = [
    {
      icon: FaFire,
      path: "ai-agents",
      label: "Агенты",
      buttons: [
        {
          icon: FaPlus,
          onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
            navigate("/admin/ai-agents/add");
          },
        },
      ],
      children: agentsState?.length > 0 ? agentsState.map((a => {
        return {
          icon: MdPayments,
          path: `ai-agents/${a.id}/settings`,
          label: a.name,
          children: [
            {
              icon: MdSettings,
              path: `ai-agents/${a.id}/settings`,
              label: "Настройки",
            },
            {
              icon: MdBookmarks,
              path: `ai-agents/${a.id}/prompts`,
              label: "Промтинг",
              disabled: authUser?.phone == "77773001994"
            },
            {
              icon: MdCode,
              path: `ai-agents/${a.id}/functions`,
              label: "Функции",
              disabled: authUser?.phone == "77773001994"
            },
            {
              icon: MdBookmarks,
              path: `ai-agents/${a.id}/knowledge-documents`,
              label: "База знаний",
              disabled: authUser?.phone == "77773001994"
            },
            {
              icon: MdIntegrationInstructions,
              path: `ai-agents/${a.id}/channels`,
              label: "Каналы",
            },
            {
              icon: MdChat,
              path: `ai-agents/${a.id}/dialogs`,
              label: "Тестовая среда",
            },
            {
              icon: MdBarChart,
              path: `ai-agents/${a.id}/stats`,
              label: "Статистика",
            },
            {
              icon: MdOutlineModeComment,
              path: `ai-agents/${a.id}/comments`,
              label: "Комментарии",
            },
          ]
        }
      })) : []
    },
  ]

  const marketplaceMenu = [
    {
      icon: MdShoppingCart,
      path: "products",
      label: "Управление товарами",
      children: [
        {
          icon: MdLocalActivity,
          path: "products/list",
          label: "Товары",
        },
        {
          icon: MdLocalActivity,
          path: "products/warehouses",
          label: "Точки продаж",
        },
        {
          icon: MdLocalActivity,
          path: "products/sub-orders",
          label: "Заказы",
        },
        {
          icon: MdLocalActivity,
          path: "products/returns",
          label: "Возвраты",
        },
      ]
    },
  ]

  const advancedAdminMenu = [
    {
      icon: MdSettings,
      path: "orgs/:id/edit".replace(':id', localStorage.getItem('orgID')),
      label: "Настройки",
    },
    {
      icon: MdSupervisorAccount,
      path: "members",
      label: "Сотрудники",
    },
    {
      icon: MdSupervisorAccount,
      path: "branches",
      label: "Филиалы",
    },
    {
      icon: MdTrain,
      path: "trains",
      label: "Поезда",
    },
    {
      icon: MdSupervisorAccount,
      path: "roles",
      label: "Роли",
    },
    {
      icon: MdIntegrationInstructions,
      path: "integrations",
      label: "Интеграции",
    },
    // Оплата и Тарифы
    {
      icon: MdPayments,
      path: "plans",
      label: "Оплата и Тарифы",
    },
    {
      icon: MdSettings,
      path: 'faq',
      label: "Вопросы-Ответы",
    },
    {
      icon: MdSettings,
      path: 'locations',
      label: "Топ локаций",
    },
    {
      icon: MdOutlineGroups,
      path: "invoices",
      label: "Счет на оплату",
    },
    // {
    //   icon: MdSupervisorAccount,
    //   path: "whatsapp/connect",
    //   label: "Whatsapp Интергация",
    // },
    {
      icon: MdOutlineNotifications,
      path: "broadcasts",
      label: "Массовая рассылки",
    },
    {
      icon: MdOutlineModeComment,
      path: "reviews",
      label: "Обратная связь",
    },
    {
      icon: MdLogout,
      path: "/auth/logout",
      label: "Выйти",
    },
  ];

  const allMenu = [
    adminMenu,
    aiAgentsMenu,
    marketplaceMenu,
    advancedAdminMenu,
  ].map((group) => {
    return group.filter((item) => {
      // Inbox — это базовый оперативный экран поддержки. Показываем всегда,
      // даже если 'inbox' пока не добавлен в org.modules.
      if (item.path === "/auth/logout" || item.path.startsWith("orgs/") || item.path === "inbox") {
        return true
      }
      return org?.modules?.includes(item.path)
    })
  });

  useEffect(() => {
    document.addEventListener("click", (event) => {
      if (!sidebar.current) {
        return;
      }
      if (!sidebar.current.contains(event.target) && !burger.current.contains(event.target)) {
        sidebar.current.classList.remove("active");
      }
    });
  }, []);

  useEffect(() => {
    sidebar.current.classList.remove("active");
  }, [location]);


  return (
    <div className="page-wrapper" id="main-wrapper">
      <aside className="left-sidebar admin-nav" ref={sidebar} style={{ width: sidebarOpen ? 300 : 50 }}>
        {sidebarOpen ? (
          <>
            <div className="admin-logo flex items-center justify-between">
              <div className="flex-1">
                <Logo />
              </div>
              <Button variant="text" onClick={() => {
                dispatch(updateSidebarAction({ open: !sidebarOpen }));
              }}>
                <FaArrowLeft />
              </Button>
            </div>
            <Box sx={{ width: '100%', maxWidth: 360 }}>
              <nav className="sidebar-nav">
                <List>
                  {allMenu.map((group, groupIndex) => {
                    return <div key={groupIndex}>
                      {group.map((item, index) => {
                        return <>
                          <div key={index} className="menu-item">
                            <ListItemButton component={NavLink} to={item.path} className={`sidebar-link`} id={`${item.path}-item`}>
                              <ListItemIcon sx={{ minWidth: '1.5rem' }}>
                                {item.icon && <item.icon id={item.path} />}
                              </ListItemIcon>
                              <ListItemText primary={item.label}></ListItemText>
                            </ListItemButton>
                            <div className="sub-menu-wrapper" timeout="auto">
                              {item.children && item.children.map((c, subInx) => {
                                return <List component="div" disablePadding className="sub-menu" key={subInx}>
                                  <ListItemButton sx={{ pl: 4 }} component={NavLink} to={c.path} className={`sidebar-link ${c.active ? 'active' : ''}`}>
                                    <ListItemIcon sx={{ minWidth: '1.5rem' }}>
                                      {item.icon && <item.icon />}
                                    </ListItemIcon>
                                    <ListItemText primary={c.label} />
                                  </ListItemButton>
                                  <Collapse in={location.pathname.includes(String(c.path).split('/').slice(0, -1).join('/')) || c.active} timeout="auto">
                                    {c.children && c.children?.filter((c) => !c.disabled).map((cc, subInx) => {
                                      return <List component="div" disablePadding className="sub-menu" key={subInx}>
                                        <ListItemButton sx={{ pl: 4 }} component={NavLink} to={cc.path} style={{ paddingLeft: '4rem' }} className={`sidebar-link ${cc.active ? 'active' : ''}`}>
                                          <ListItemIcon sx={{ minWidth: '1.5rem' }}>
                                            {cc.icon && <cc.icon />}
                                          </ListItemIcon>
                                          <ListItemText primary={cc.label} />
                                        </ListItemButton>
                                      </List>
                                    })}
                                  </Collapse>
                                </List>
                              })}
                            </div>
                          </div>
                        </>
                      })}
                      <Divider style={{ margin: '10px 0' }} />
                    </div>
                  })}
                </List>
                <Divider className="my-2 mt-4" />
                <section className="text-center fs-14 p-16">
                  <a className="color-blue" style={{
                    lineHeight: '2',
                    display: 'block',
                    padding: 0,
                    margin: 0
                  }} href="https://hajjtravel.kz/privacy" target="_blank">Privacy Policy</a>
                  <a className="color-blue" href="https://hajjtravel.kz/terms" target="_blank">Terms of Service</a>
                  <div className="m-t-8">{new Date().getFullYear()}. All rights reserved.</div>
                </section>
              </nav>
            </Box>
          </>
        ) : (
          <div className="sidebar-close p-2">
            <FaArrowRight style={{
              width: '100%',
            }} onClick={() => {
              dispatch(updateSidebarAction({ open: !sidebarOpen }));
            }} />
            <section className="mt-8">
              {allMenu.map((item, index) => {
                return <div key={index} className="flex flex-col" style={{ gap: '6px' }}>
                  {item.map((i, iInx) => {
                    return <div key={iInx}>
                      <NavLink to={i.path} className="sidebar-link flex items-center justify-center text-center" style={{ padding: '2px' }}>
                        <ListItemIcon sx={{ minWidth: '1.5rem', height: '1.5rem' }} className="flex items-center justify-center">
                          {i.icon && <i.icon color="primary" />}
                        </ListItemIcon>
                      </NavLink>
                    </div>
                  })}
                </div>
              })}
            </section>
          </div>
        )}
      </aside>
      <section className={`admin-content ${sidebarOpen ? 'opened' : 'closed'}`}>
        <div id="admin-header" className="ml-auto flex justify-between" style={{ padding: "12px 0" }}>
          <div className="flex-1 no-mobile">
            <CurrencyRates></CurrencyRates>
          </div>
          <div className="flex">
            <UserMenu
              user={authUser}
              setMobileOpen={() => {
                sidebar.current.classList.toggle("active");
              }}
            />
            <IconButton
              ref={burger}
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => {
                sidebar.current.classList.toggle("active");
              }}
              sx={{ display: { sm: "none" } }}
            >
              <FaBars></FaBars>
            </IconButton>
          </div>
        </div>

        {/* {isSubscriptionFetched && !subscriptionData && (
          <div id="notification" className="py-4">
            <Alert severity="info" className="flex items-center gap-2">
              <div className="flex items-center justify-between gap-2">
                Пожалуйста, подпишитесь на тариф, чтобы продолжить использование сервиса.
                <Button variant="text" color="primary" onClick={() => {
                  navigate('/admin/plans');
                }}>
                  Подписаться
                </Button>
              </div>
            </Alert>
          </div>
        )} */}
        <Outlet />
      </section>
    </div >
  );
};

export default withUser(AdminLayout);
